'use client';

import { BoxScrolled, usePortalContext } from '@cloai/uikit';
import _ from 'lodash';
import { useTranslations } from 'next-intl';
import { SyntheticEvent } from 'react';

import { PORTALS } from '@/.constants/portals';
import { Style, StyleCategory } from '@/.gql/graphql';
import StyleIcon from '@/public/icons/person.svg';
import { SeeAllButton } from '@/src/components/SeeAllButton';
import { useVitrinaPosition } from '@/src/hooks/useVitrinaPosition';
import {
  useProfileContext,
  useStyleContext,
  useGTMContext,
} from '@/src/providers';
import { getImageByGender } from '@/src/utils/getImageByGender';

import { PackPreview } from './PackPreview';

const styles = {
  wrapper: 'flex flex-col gap-3 flex-1 scroll-mt-[150px]',
  line: 'flex items-center gap-2',
  lineTitle: 'text-lg font-bold',
  lineIcon: 'w-4 h-4',
  lineCount: 'text-sm font-medium text-label-secondary',
  category: 'flex flex-col gap-3',
  head: 'flex items-center justify-between',
  name: 'text-lg font-bold',
  styleItem: 'shrink-0 relative overflow-hidden rounded-lg',
  preview: 'w-32 h-[152px] object-cover object-top',
  tag: 'absolute top-1 right-1',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-sm',
  info: 'absolute left-3 bottom-3',
  styleName: 'mb-[2px] text-sm font-medium text-left',
};

export const ImagePack = () => {
  const t = useTranslations('MenuPage');
  const profile = useProfileContext()!;
  const sendGTM = useGTMContext();
  const { openPortal } = usePortalContext();
  const {
    styleCategories,
    changeActiveCategory,
    changeActiveStyle,
    genderMode,
    setPortalSeeAllMode,
    setPrevPortal,
  } = useStyleContext();

  const vitrinaPosition = useVitrinaPosition('styles');

  const handleClickPack = (event: SyntheticEvent) => {
    const categoryId = event.currentTarget.getAttribute('data-category-id');
    if (categoryId) {
      changeActiveCategory(categoryId, 'style');
    }

    setPrevPortal('main');
    setPortalSeeAllMode('styles');
    openPortal(PORTALS.SeeAllImages);
  };

  const handleClickImage = (event: SyntheticEvent) => {
    const categoryId = event.currentTarget.getAttribute('data-category-id');

    if (categoryId) {
      changeActiveCategory(categoryId, 'style');
    }

    const styleId = event.currentTarget.getAttribute('data-style-id');
    if (styleId) {
      changeActiveStyle(styleId, 'styles');
    }

    const currentCategory = _.find(styleCategories, (category) =>
      _.find(category.styles, { id: styleId }),
    );

    const currentStyle = _.find(_.get(currentCategory, 'styles', []), {
      id: styleId,
    });

    if (currentCategory && currentStyle) {
      const currentIndex = (currentCategory as StyleCategory).styles?.findIndex(
        (style) => style.id === styleId,
      );
      sendGTM({
        event: 'menu_selection',
        block: 'Стили',
        type: (currentStyle as Style).__typename,
        category_name: (currentCategory as StyleCategory).name['en'],
        name: (currentStyle as Style).name['en'],
        category_position: currentIndex + 1,
        vitrina_position: vitrinaPosition,
        gender: genderMode,
      });
    }

    setPrevPortal('main');
    setPortalSeeAllMode('styles');
    openPortal(PORTALS.GenerateImage);
  };

  if (!styleCategories?.length) {
    return;
  }

  return (
    <div className={styles.wrapper} id="styles">
      <div className={styles.line}>
        <StyleIcon className={styles.lineIcon} />
        <div className={styles.lineTitle}>{t('stylesTitle')}</div>
        <div className={styles.lineCount}>{t('photosCount')}</div>
      </div>
      {styleCategories?.map((category, ind) => (
        <div className={styles.category} key={category.id}>
          <div className={styles.head}>
            <div className={styles.name}>{category.name[profile?.lang]}</div>
            <SeeAllButton onClick={handleClickPack} categoryId={category.id} />
          </div>
          <BoxScrolled
            direction="horizontal"
            hideScrollbar
            gap={8}
            offsetOut={20}
          >
            {category.styles.map((item, i) => (
              <PackPreview
                key={item.id}
                id={item.id}
                index={i}
                categoryIndex={ind}
                categoryId={category.id}
                isPopular={item.isPopular}
                isNew={item.isNew}
                name={item.name[profile?.lang]}
                image={getImageByGender(item, genderMode) ?? ''}
                handleClickImage={handleClickImage}
                __typename={item.__typename}
              />
            ))}
          </BoxScrolled>
        </div>
      ))}
    </div>
  );
};
